import type { ConsoleTone } from '@/components/console/tone'

/**
 * 线索域常量:阶段字典 / 阶段色板 / 合法迁移矩阵 / 排序与分页取值面。
 *
 * 阶段与迁移矩阵是后端 `apps/crm_apps/crm_core/services.py` 的 `LEAD_LEGAL_TRANSITIONS` 的前端镜像
 * ——用途只有一个:**只渲染合法的下一步按钮**。真正的判据仍在服务层(义务 A①②),
 * 前端猜错也不会写脏数据,后端会以中文 400 拒收。
 */

/** 六阶段(与 LeadStatusEnum 同序,展示序即业务必经序)。 */
export const LEAD_STAGE_VALUES = ['new', 'contacted', 'proposal_sent', 'negotiating', 'won', 'lost'] as const

export type LeadStage = (typeof LEAD_STAGE_VALUES)[number]

/** 兜底中文名:后端 /meta/enums 未就绪时用它,取到枚举后一律以后端 label 为准。 */
export const LEAD_STAGE_FALLBACK_LABELS: Record<LeadStage, string> = {
  new: '新线索',
  contacted: '已联系',
  proposal_sent: '方案已发',
  negotiating: '洽谈中',
  won: '成单',
  lost: '流失',
}

/**
 * 阶段色板(new 灰 / contacted 蓝 / proposal_sent 青 / negotiating 橙 / won 绿 / lost 红)。
 * 这里只声明 tone,类名住在 `components/console/tone.ts`(全站唯一色板)。
 */
export const LEAD_STAGE_TONE: Record<LeadStage, ConsoleTone> = {
  new: 'neutral',
  contacted: 'info',
  proposal_sent: 'processing',
  negotiating: 'warning',
  won: 'success',
  lost: 'danger',
}

/** 合法迁移矩阵:必经序 + 任一中间态可直接 lost;won/lost 为不可逆终态。 */
export const LEAD_LEGAL_TRANSITIONS: Record<LeadStage, readonly LeadStage[]> = {
  new: ['contacted', 'lost'],
  contacted: ['proposal_sent', 'lost'],
  proposal_sent: ['negotiating', 'lost'],
  negotiating: ['won', 'lost'],
  won: [],
  lost: [],
}

export function isLeadStage(value: string): value is LeadStage {
  return (LEAD_STAGE_VALUES as readonly string[]).includes(value)
}

/**
 * 产品族(★feat-008-C W3,需求 7)。
 *
 * 后端是 `Lead.ProductFamily` 的 choices,**不建字典表**、经 `/meta/enums` 的
 * `product_families` 下发。这里只留**兜底中文名**(与阶段字典同律:字典未到位时的首帧),
 * 取值面一律以后端下发为准 —— 前端不另立第二真源。
 */
export const PRODUCT_FAMILY_VALUES = ['day_trip', 'custom_tour'] as const

export type ProductFamily = (typeof PRODUCT_FAMILY_VALUES)[number]

export const PRODUCT_FAMILY_FALLBACK_LABELS: Record<ProductFamily, string> = {
  day_trip: '1日游',
  custom_tour: '定制游 3-15 天',
}

/** 跟进类型(SalesActivity.ActivityType)。 */
export const ACTIVITY_TYPE_VALUES = ['follow_up', 'call', 'visit'] as const

export type ActivityType = (typeof ACTIVITY_TYPE_VALUES)[number]

export const ACTIVITY_TYPE_FALLBACK_LABELS: Record<ActivityType, string> = {
  follow_up: '跟进',
  call: '通话',
  visit: '到访',
}

/* -------------------------------------------------------------------------- */
/* 建档表单用的字典(后端没有单独下发端点的那几个)                              */
/* -------------------------------------------------------------------------- */

/** 客户类型(Customer.CustomerType);`/meta/enums` 不下发本组,故在前端固化。 */
export const CUSTOMER_TYPE_OPTIONS = [
  { value: 'individual', label: '个人' },
  { value: 'company', label: '企业' },
] as const

/**
 * 外部身份来源的兜底中文名(ExternalIdentity.Source)。
 * 正常取值面来自 `/meta/enums` 的 `identity_sources`,这里只在字典未到位时兜底。
 */
export const IDENTITY_SOURCE_FALLBACK_LABELS: Record<string, string> = {
  xhs: '小红书',
  wechat: '微信',
  email: '邮件',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  douyin: '抖音',
  website_chat: '网站在线咨询',
  salesmartly: 'SalesSmartly 聚合平台联系人',
}

/** 建档表单的买方模式:选既有客户 / 快捷新建客户(后端「买方二选一」的 UI 面)。 */
export const LEAD_BUYER_MODES = ['existing', 'new'] as const

export type LeadBuyerMode = (typeof LEAD_BUYER_MODES)[number]

/** 后端 ordering 白名单(LEAD_ORDERING_FIELDS)× 升降序,前缀 `-` 为降序。 */
export const LEAD_ORDERING_VALUES = [
  '-create_time',
  'create_time',
  '-update_time',
  'update_time',
  '-status',
  'status',
  '-travel_start_date',
  'travel_start_date',
] as const

export type LeadOrdering = (typeof LEAD_ORDERING_VALUES)[number]

/** 可排序列 → ordering 字段名(列 id 与 ordering 白名单的唯一映射)。 */
export const LEAD_SORTABLE_COLUMNS: Record<string, 'create_time' | 'update_time' | 'status' | 'travel_start_date'> = {
  status: 'status',
  travel_start_date: 'travel_start_date',
  create_time: 'create_time',
}

/** 每页条数(后端 max_page_size=100,超限会被后端截断,故不放更大的档)。 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

export const DEFAULT_PAGE_SIZE = 20

export const DEFAULT_LEAD_ORDERING: LeadOrdering = '-create_time'

/**
 * Radix Select 不接受空串 item value,故「全部」用哨兵值表示;
 * 写回 URL 前统一翻译成 undefined(= 该筛选不出现在 search params 里)。
 */
export const ALL_OPTION_VALUE = '__all__'

/**
 * 「未定 / 未采到」筛选档的哨兵(后端同名:`?product_family=none` / `?campaign=none`)。
 *
 * 为什么需要它:可空列上「空」是与任一具体取值并列的一档,而 `?product_family=` 在 HTTP 上
 * 与「不传该参数」不可区分。销售的真实诉求是「哪些线索还没问出产品族 / 还没采到活动号」,
 * 没有这一档就只能翻页人眼找。哨兵值与后端一字相同,URL 直传不做翻译。
 */
export const NONE_OPTION_VALUE = 'none'

/** 详情页成单对话框的两种回执模式(D:crm-online 适配点)。 */
export const WON_RECEIPT_MODES = ['order', 'deal'] as const

export type WonReceiptMode = (typeof WON_RECEIPT_MODES)[number]

/* -------------------------------------------------------------------------- */
/* 成单回执的四条状态轴(★feat-008 W15,R3-B;won-transition-design §3.4)        */
/* -------------------------------------------------------------------------- */

/**
 * 收款轴的合法迁移表 —— 后端 `crm_core/services.py::WON_RECEIPT_PAYMENT_TRANSITIONS`
 * 的前端镜像。用途与 `LEAD_LEGAL_TRANSITIONS` 一模一样:**只渲染当前状态的合法下一步**。
 * 真正的判据仍在服务层,前端猜错也写不脏数据 —— 后端会以中文 400 拒收。
 *
 * ⚠️ 两个终局各自映射空数组:`paid` 是收款轴终态(录错=补新回执行,不原地改数),
 * `waived` 同样是终局(它是收款轴的另一个终局,不是能再走出去的中间态)。
 * 空数组 ⇒ 该轴一个按钮都不渲染,而不是渲染一排点下去必然 400 的按钮。
 */
export const WON_RECEIPT_PAYMENT_TRANSITIONS: Record<string, readonly string[]> = {
  pending: ['partially_paid', 'paid', 'overdue', 'waived'],
  partially_paid: ['paid', 'overdue', 'waived'],
  overdue: ['partially_paid', 'paid', 'waived'],
  paid: [],
  waived: [],
}

/** 出行轴:`not_started → ready → traveling → returned`,**允许跳档前进、禁回退**,`returned` 终态。 */
export const WON_RECEIPT_TRAVEL_TRANSITIONS: Record<string, readonly string[]> = {
  not_started: ['ready', 'traveling', 'returned'],
  ready: ['traveling', 'returned'],
  traveling: ['returned'],
  returned: [],
}

/*
 * ★W15b:展示阶梯的那格哨兵值原先在此手抄一份 `WON_RECEIPT_STAGE_UNKNOWN = 'unknown'`。
 * 后端把 `stage` 的取值域声明进契约(`WonReceiptStageEnum`)之后它已无立足理由 ——
 * 消费点改 import 生成物的 `WonReceiptStageEnum.unknown`,拼错当场 TS 报错而不是运行期静默。
 */

/** 生命周期轴 / 终账轴的取值域(照抄后端 `WON_RECEIPT_LIFECYCLE_STATUSES` / `..._SETTLEMENT_STATUSES`)。 */
export const WON_RECEIPT_LIFECYCLE_STATUSES = ['confirmed', 'cancelled'] as const
export const WON_RECEIPT_SETTLEMENT_STATUSES = ['unsettled', 'settled'] as const

/** 详情页 tab(状态进 search param)。★W11 加 `dispatch`=派发时间线(LD-21:与阶段时间线并列成 tab)。 */
export const LEAD_DETAIL_TABS = ['activities', 'timeline', 'dispatch'] as const

export type LeadDetailTab = (typeof LEAD_DETAIL_TABS)[number]

/* -------------------------------------------------------------------------- */
/* 回收池(★feat-008 W11;lead-dispatch-design §3.1 两种池态 / §6.2 池视图)      */
/* -------------------------------------------------------------------------- */

/**
 * 池态二值(后端 `?pool=` 的取值面,同名直传,不做翻译)。
 *
 * 「总池子」入口本身**零新参数** —— 它就是既有的 `?owner=none`(哨兵早在)。
 * 本组只做池内的二分:`recalled`=被主管收回进池的 / `never`=从未派发过的
 * (进线时自动分配没跑成,或建档时就没给)。两者在主档上**逐字节相同**
 * (`owner_employee = NULL`),差别只存在于派发史链尾 —— 这正是 S4-18 要自建派发史的理由。
 */
export const LEAD_POOL_STATE_VALUES = ['recalled', 'never'] as const

export type LeadPoolState = (typeof LEAD_POOL_STATE_VALUES)[number]

export const LEAD_POOL_STATE_LABELS: Record<LeadPoolState, string> = {
  recalled: '已收回',
  never: '未曾派发',
}

/** 池态徽标色:已收回=警示(有人没跟进)/ 未曾派发=中性(不指向任何人)。 */
export const LEAD_POOL_STATE_TONE: Record<LeadPoolState, ConsoleTone> = {
  recalled: 'warning',
  never: 'neutral',
}

export function isLeadPoolState(value: string | null | undefined): value is LeadPoolState {
  return typeof value === 'string' && (LEAD_POOL_STATE_VALUES as readonly string[]).includes(value)
}

/** 派发动作六值的兜底中文名(真源是后端 `assignment_kind_display`,这里只兜首帧/降级)。 */
export const ASSIGNMENT_KIND_FALLBACK_LABELS: Record<string, string> = {
  auto_assign: '自动分配',
  manual_assign: '手工指派',
  manual_reassign: '手工转派',
  recall_to_pool: '收回总池子',
  pool_dispatch: '二次派发',
  offboard_handover: '离职交接',
}

/** 派发动作徽标色:回池=警示 / 二次派发=处理中 / 其余=信息或中性。 */
export const ASSIGNMENT_KIND_TONE: Record<string, ConsoleTone> = {
  auto_assign: 'info',
  manual_assign: 'info',
  manual_reassign: 'info',
  recall_to_pool: 'warning',
  pool_dispatch: 'processing',
  offboard_handover: 'neutral',
}

/**
 * 再派发对话框的两种接手人模式。
 * `rotation` = 留空由后端按轮转游标挑人(与新线索自动分配**同一个游标**,LD-18);
 * `named`    = 主管点名(不受「排除上一手」护栏约束 —— LD-19 只作用于自动挑人)。
 */
export const POOL_DISPATCH_MODES = ['rotation', 'named'] as const

export type PoolDispatchMode = (typeof POOL_DISPATCH_MODES)[number]
